<template>
  <div>
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters @show-modal="$root.$emit('bv::show::modal', $event)" :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters" />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters :active-filter.sync="activeFilter" :start-date.sync="startDate" :end-date.sync="endDate" :counter-filter.sync="counterFilter"/>
      </template>
    </filter-modal>

    <toastification-loading-content ref="loadingToast" />

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block mr-1" :placeholder="$t('table.search') + '...'"
                @keypress.enter="refetchData" />
              <b-button @click="exportExcel()" variant="success" class="text-capitalize ml-1" type="button"
                :disabled="buttonExport">
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table ref="refListTable" class="position-relative" :items="fetchList" responsive :fields="tableColumns"
        primary-key="id" :sort-by.sync="sortBy" show-empty :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc">
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>

            <b-dropdown-item @click="resetCounter(data.item.id)">
              <feather-icon icon="RefreshCwIcon" />
              <span class="align-middle ml-50">{{ $t("traffic.reset") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <Pagination :totalList="totalList" :currentPage="currentPage" @updatePagination="update" :perPage="perPage">
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import useList from "./useList";
import store from "@/store";
import Filters from "./Filters.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import moment from "moment-timezone";
import { useToast } from "vue-toastification/composition";


export default {
  components: {
    Filters,
    BreadcrumbMenu,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
    Pagination,
    FilterModal,
    ButtonFilters,
  },
  setup(_, context) {
    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination
    const toast = useToast();
    const { t } = useI18nUtils();
    const trans = {
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
    };

    // start breadcrumb and export
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        trans["messages.export.download"],
        trans["messages.export.Generating_file"],
        "secondary"
      );
      store
        .dispatch("traffic/export", {
          q: searchQuery.value,
          size: perPage.value,
          page: currentPage.value,
          sortBy: sortBy.value === 'createdAt' ? 'created_at' : (sortBy.value || 'ip'),
          sortDesc: isSortDirDesc.value ? 1 : 0,
          alarm: activeFilter.value,
          counter: counterFilter.value,
          updatedAtMin: startDate.value ? moment(startDate.value).format("YYYY-MM-DDTHH:mm") : null,
          updatedAtMax: endDate.value ? moment(endDate.value).format("YYYY-MM-DDTHH:mm") : null,
          action: action.value || 'list',
          action: "export",
        })
        .then((response) => {
          console.log(response);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };
    const loadingToast = ref(null);
    const action = ref("list");
    const buttonExport = ref(false);
    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      startDate,
      endDate,
      counterFilter,
      activeFilter,
      buildFilters,
      clearSearchFilters,
      filtersCount,
    } = useList();


    // resetCounter shows a modal to confirm reseting counter
    const resetCounter = function (id) {
      const storeInner = store;
      const self = this;
      this.$swal({
        title: context.parent.$i18n.t("messages.reset.counter_title"),
        text: context.parent.$i18n.t("messages.reset.counter"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: context.parent.$i18n.t("actions.reset"),
        cancelButtonText: context.parent.$i18n.t("actions.cancel"),
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("traffic/resetCounter", id)
            .then((response) => {
              toast({
                component: ToastificationContent,
                props: {
                  title: context.parent.$i18n.t("messages.actions.executed_successfully").replace("{action}", context.parent.$i18n.t("actions.reset")),
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              self.refetchData();
            })
            .catch((err) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: context.parent.$i18n.t("messages.error.server_error"),
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    return {
      buttonExport,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      activeFilter,
      counterFilter,
      startDate,
      endDate,
      exportExcel,
      loadingToast,
      action,
      update,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      resetCounter,
    };
  },
};
</script>
